import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiPlus,
  FiSave,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import InventoryProductPicker from './InventoryProductPicker';

const emptyRow = () => ({
  variantId: "",
  quantityRequested: "",
  notes: "",
});

const number = (value) => Number(value || 0);

const quantity = (value) =>
  number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });

const today = () => new Date().toISOString().slice(0, 10);

const variantLabel = (balance) =>
  [
    balance.variant?.product?.name,
    balance.variant?.color?.name,
    balance.variant?.size?.name,
    balance.variant?.sku,
  ]
    .filter(Boolean)
    .join(" · ");

export default function StockTransferPage() {
  const { accessToken, isLoading: isAuthLoading } = useAuth();

  const [stores, setStores] = useState([]);
  const [balances, setBalances] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [sourceStoreId, setSourceStoreId] = useState("");
  const [destinationStoreId, setDestinationStoreId] = useState("");
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftTransfer, setDraftTransfer] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const [storesData, balancesData, transfersData] = await Promise.all([
        apiRequest("/inventory/stores", { accessToken }),
        apiRequest("/inventory/balances", { accessToken }),
        apiRequest("/inventory/transfers", { accessToken }),
      ]);

      setStores(storesData || []);
      setBalances(balancesData || []);
      setTransfers(transfersData || []);
    } catch (err) {
      setError(err.message || "Unable to load transfer information.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadData();
  }, [isAuthLoading, accessToken, loadData]);

  const sourceBalances = useMemo(
    () => balances.filter((balance) => balance.storeId === sourceStoreId),
    [balances, sourceStoreId],
  );

  const balanceByVariantId = useMemo(
    () =>
      new Map(sourceBalances.map((balance) => [balance.variantId, balance])),
    [sourceBalances],
  );

  const stats = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const transfersToday = transfers.filter(
      (transfer) => new Date(transfer.createdAt) >= todayStart,
    ).length;

    const awaitingReceipt = transfers.filter((transfer) =>
      ["DISPATCHED", "PARTIALLY_RECEIVED"].includes(transfer.status),
    ).length;

    const completed = transfers.filter(
      (transfer) => transfer.status === "RECEIVED",
    ).length;

    const unitsMoved = transfers.reduce(
      (total, transfer) =>
        total +
        (transfer.items || []).reduce(
          (itemTotal, item) => itemTotal + number(item.quantityDispatched),
          0,
        ),
      0,
    );

    return {
      transfersToday,
      awaitingReceipt,
      completed,
      unitsMoved,
    };
  }, [transfers]);

  const addRow = () => {
    setRows((previous) => [...previous, emptyRow()]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) {
      setError("A transfer needs at least one product.");
      return;
    }

    setRows((previous) => previous.filter((_, rowIndex) => rowIndex !== index));
  };

  const updateRow = (index, field, value) => {
    setRows((previous) =>
      previous.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const changeSourceStore = (value) => {
    setSourceStoreId(value);
    setRows([emptyRow()]);
    setDraftTransfer(null);
    setError("");
    setSuccess("");
  };

  const validateTransfer = () => {
    if (!sourceStoreId || !destinationStoreId) {
      setError("Select both source and destination locations.");
      return false;
    }

    if (sourceStoreId === destinationStoreId) {
      setError("Source and destination stores must be different.");
      return false;
    }

    const variantIds = rows.map((row) => row.variantId);

    if (variantIds.some((variantId) => !variantId)) {
      setError("Select a product variant in every row.");
      return false;
    }

    if (new Set(variantIds).size !== variantIds.length) {
      setError("The same variant cannot appear twice in one transfer.");
      return false;
    }

    if (
      rows.some(
        (row) => !row.quantityRequested || number(row.quantityRequested) <= 0,
      )
    ) {
      setError("Enter a transfer quantity greater than zero in every row.");
      return false;
    }

    if (
      rows.some((row) => {
        const balance = balanceByVariantId.get(row.variantId);

        return number(row.quantityRequested) > number(balance?.quantityOnHand);
      })
    ) {
      setError("Transfer quantity cannot exceed the source stock.");
      return false;
    }

    return true;
  };

  const saveDraft = async () => {
    setError("");
    setSuccess("");

    if (!validateTransfer()) return;

    try {
      setSaving(true);

      const transfer = await apiRequest("/inventory/transfers", {
        method: "POST",
        accessToken,
        body: {
          sourceStoreId,
          destinationStoreId,
          notes: notes.trim() || undefined,
          items: rows.map((row) => ({
            variantId: row.variantId,
            quantityRequested: number(row.quantityRequested),
            notes: row.notes.trim() || undefined,
          })),
        },
      });

      setDraftTransfer(transfer);
      setSuccess(
        `Draft ${transfer.transferNumber} saved. Review it, then dispatch stock.`,
      );

      await loadData();
    } catch (err) {
      setError(err.message || "Unable to create transfer draft.");
    } finally {
      setSaving(false);
    }
  };

  const dispatchTransfer = async () => {
    if (!draftTransfer?.id) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const transfer = await apiRequest(
        `/inventory/transfers/${draftTransfer.id}/dispatch`,
        {
          method: "POST",
          accessToken,
        },
      );

      setDraftTransfer(transfer);
      setSuccess(
        `${transfer.transferNumber} dispatched successfully. The destination store can now receive it.`,
      );

      await loadData();
    } catch (err) {
      setError(err.message || "Unable to dispatch transfer.");
    } finally {
      setSaving(false);
    }
  };

  const startNewTransfer = () => {
    setSourceStoreId("");
    setDestinationStoreId("");
    setNotes("");
    setRows([emptyRow()]);
    setDraftTransfer(null);
    setError("");
    setSuccess("");
  };

  const formLocked = Boolean(draftTransfer);

  return (
    <div>
      {pickerOpen && <InventoryProductPicker open onClose={() => setPickerOpen(false)} storeId={sourceStoreId} balances={balances} accessToken={accessToken} onAdd={selected => setRows(current => {
        const existing = current.filter(row => row.variantId);
        const ids = new Set(existing.map(row => row.variantId));
        return [...existing, ...selected.filter(row => !ids.has(row.variantId)).map(row => ({ ...emptyRow(), variantId: row.variantId, quantityRequested: '1' }))];
      })} />}
      <div className="mb-8">
        <p className="text-sm text-gray-500">Inventory / Stock Transfer</p>

        <h1 className="mt-1 text-3xl font-bold">Stock Transfer</h1>

        <p className="mt-2 text-gray-500">
          Create a draft, dispatch stock, then receive it at the destination.
        </p>
      </div>

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <Card title="Transfers Today" value={stats.transfersToday} />
        <Card
          title="Awaiting Receipt"
          value={stats.awaitingReceipt}
          color="text-orange-600"
        />
        <Card
          title="Completed"
          value={stats.completed}
          color="text-green-600"
        />
        <Card
          title="Units Moved"
          value={quantity(stats.unitsMoved)}
          color="text-primary-700"
        />
      </div>

      {error && (
        <div className="mb-6 rounded-2xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-2xl bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Transfer Information</h2>

        <div className="grid gap-4 lg:grid-cols-4">
          <input
            value={
              draftTransfer?.transferNumber ||
              "Generated automatically when draft is saved"
            }
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-500"
          />

          <input
            type="date"
            value={today()}
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />

          <select
            value={sourceStoreId}
            disabled={loading || formLocked}
            onChange={(event) => changeSourceStore(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">From store / warehouse</option>

            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} · {store.type}
              </option>
            ))}
          </select>

          <select
            value={destinationStoreId}
            disabled={loading || formLocked}
            onChange={(event) => setDestinationStoreId(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">To store / warehouse</option>

            {stores
              .filter((store) => store.id !== sourceStoreId)
              .map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name} · {store.type}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex items-center justify-between border-b border-gray-100 p-6">
          <div>
            <h2 className="text-lg font-semibold">Transfer Products</h2>

            <p className="mt-1 text-sm text-gray-500">
              Products are selected from the source location’s stock.
            </p>
          </div>

          <button
            type="button"
            disabled={!sourceStoreId || formLocked}
            onClick={() => setPickerOpen(true)}
            className="flex h-10 items-center gap-2 rounded-xl bg-primary-700 px-4 text-white disabled:opacity-50"
          >
            <FiPlus />
            Find products
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Product Variant
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Available Stock
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Transfer Qty
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Remarks
                </th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, index) => {
                const balance = balanceByVariantId.get(row.variantId);

                return (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="min-w-80 px-6 py-4">
                      <select
                        value={row.variantId}
                        disabled={!sourceStoreId || formLocked}
                        onChange={(event) =>
                          updateRow(index, "variantId", event.target.value)
                        }
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      >
                        <option value="">Select product variant</option>

                        {sourceBalances.map((item) => (
                          <option key={item.variantId} value={item.variantId}>
                            {variantLabel(item)}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td className="px-6 py-4 font-semibold text-gray-700">
                      {row.variantId ? quantity(balance?.quantityOnHand) : "—"}
                    </td>

                    <td className="px-6 py-4">
                      <input
                        type="number"
                        min="0.001"
                        step="0.001"
                        value={row.quantityRequested}
                        disabled={!row.variantId || formLocked}
                        onChange={(event) =>
                          updateRow(
                            index,
                            "quantityRequested",
                            event.target.value,
                          )
                        }
                        className="h-11 w-32 rounded-xl border border-gray-200 px-4"
                      />
                    </td>

                    <td className="px-6 py-4">
                      <input
                        value={row.notes}
                        disabled={formLocked}
                        onChange={(event) =>
                          updateRow(index, "notes", event.target.value)
                        }
                        placeholder="Optional note"
                        className="h-11 w-full rounded-xl border border-gray-200 px-4"
                      />
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        disabled={formLocked}
                        onClick={() => removeRow(index)}
                        className="rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-40"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sourceStoreId && sourceBalances.length === 0 && (
          <div className="border-t border-gray-100 p-5 text-sm text-gray-500">
            The source location has no inventory balances available for
            transfer.
          </div>
        )}
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-4 font-semibold">Transfer Notes</h2>

        <textarea
          rows="4"
          value={notes}
          disabled={formLocked}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Transfer notes..."
          className="w-full rounded-xl border border-gray-200 p-4 outline-none focus:border-primary-700 disabled:bg-gray-50"
        />
      </div>

      <div className="flex flex-wrap justify-end gap-3">
        {draftTransfer ? (
          <>
            <button
              type="button"
              onClick={startNewTransfer}
              className="h-11 rounded-xl border border-gray-200 px-6"
            >
              New Transfer
            </button>

            {draftTransfer.status === "DRAFT" && (
              <button
                type="button"
                disabled={saving}
                onClick={dispatchTransfer}
                className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
              >
                <FiTruck />
                {saving ? "Dispatching…" : "Dispatch Transfer"}
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            disabled={saving || loading}
            onClick={saveDraft}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
          >
            <FiSave />
            {saving ? "Saving…" : "Save Draft"}
          </button>
        )}
      </div>

      <p className="mt-4 text-center text-xs text-gray-400">
        Transfer lifecycle: Draft → Dispatched → Partially Received / Received.
        Receiving is handled from the destination-side transfer screen next.
      </p>
    </div>
  );
}

function Card({ title, value, color = "text-gray-900" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className={`mt-2 text-3xl font-bold ${color}`}>{value}</h3>
    </div>
  );
}
